import React, { Suspense, SVGAttributes, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { pxToRem } from '../../../utils/styles';
import { ColorTypes } from '../../themes/_declarations/colors';
import { IconKey } from '../../themes/_declarations/icons';
import { ThemeType } from '../../themes/_declarations/theme';

/**
 *
 * ICON SIZING:
 *
 * xs -> 16px;
 * sm -> 24px;
 * md -> 32px;
 * lg -> 40px;
 *
 */

const DEFAULT_SIZE = 'sm'; // 24px
const DEFAULT_ICON_COLOR = 'black';

export type IconSizeType = 'lg' | 'md' | 'sm' | 'xs';

export const getIconSize = (size: IconSizeType | number | undefined): number =>
  typeof size === 'number'
    ? size
    : {
        lg: 40,
        md: 32,
        sm: 24,
        xs: 16
      }[size ?? DEFAULT_SIZE];

export type IconProps = SVGAttributes<SVGElement> & {
  iconColor?: ColorTypes;
  iconName: IconKey;
  iconSize?: IconSizeType | number;
  title?: string;
};

const Icon: React.FC<IconProps> = ({
  iconColor = DEFAULT_ICON_COLOR,
  iconName,
  iconSize,
  title,
  ...otherProps
}) => {
  const theme: ThemeType = useTheme();

  const BaseIcon = useMemo(
    () => React.lazy(() => import(`../../themes/MainTheme/icons/${iconName}`)),
    [iconName]
  );

  const size = pxToRem(getIconSize(iconSize));

  const themeColor = theme?.colors[iconColor];
  const baseIconProps = { ...otherProps, color: themeColor };
  const titleId = title
    ? `${title}_${Math.random().toString(36).slice(2, 7)}`
    : iconName;

  return (
    <Suspense>
      <BaseIcon
        aria-label={title}
        data-testid={iconName}
        height={size}
        title={title}
        titleId={titleId}
        width={size}
        {...baseIconProps}
      />
    </Suspense>
  );
};

export default Icon;
